<template>
    <div class="ticket-count-time-series-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>Sessions Overall</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <StackedAreaChart
            :title="$t('report.chatCountTimeseries')"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-if="!tableMode"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard :title="$t('report.total')" :value="stats.TotalSessionCount" unit="Ticket" />
            <StatCard :title="getTitle('average')" :value="stats.AverageSessionCount" unit="Ticket" />
            <StatCard :title="getTitle('max')" :value="stats.MaxSessionCount" unit="Ticket" />
            <StatCard :title="getTitle('min')" :value="stats.MinSessionCount" unit="Ticket" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";

const log = log2("TicketCountTimeSeriesChart");
export default Vue.extend({
    name: "TicketCountTimeSeriesChart",
    components: { ChartDoddleIcon, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.ticketCount")],
            stats: {
                TotalSessionCount: 0,
                AverageSessionCount: 0,
                MaxSessionCount: 0,
                MinSessionCount: 0,
            },
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        getTitle(type) {
            const hourlyText = this.$t("report.hourly");
            if (this.getStats().isHourlyAverage) {
                return `${this.$t(`report.${type}`)} ${hourlyText}`;
            }
            return this.$t(`report.${type}`);
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".ticket-count-time-series-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                    // scopedSlots: { customRender: "date" },
                },
                {
                    title: this.$t("report.ticketCount"),
                    dataIndex: "ticketCount",
                    key: "ticketCount",
                    // scopedSlots: { customRender: "ticketCount" },
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.data;
            log("getRows.buckets", buckets);
            const rows = buckets.map((bucket) => {
                const ticketCount = bucket.ticketCount;

                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    ticketCount,
                };
            });
            return rows;
        },
        getStats() {
            const startDate = moment(this.filters.startDate);
            const endDate = moment(this.filters.endDate);
            const buckets = this.data?.buckets || [];
            log("getStats.buckets", buckets);
            const ticketCounts = buckets.map((x) => x.ticketCount);
            const isEmpty = ticketCounts.length === 0;
            const TotalSessionCount = isEmpty ? 0 : _.chain(ticketCounts).sum().value();
            const AverageSessionCount = isEmpty ? 0 : parseInt(_.mean(ticketCounts).toFixed(1));
            const MaxSessionCount = isEmpty ? 0 : parseInt(_.max(ticketCounts).toFixed(1));
            const MinSessionCount = isEmpty ? 0 : _.min(ticketCounts).toFixed(1);

            return {
                TotalSessionCount,
                AverageSessionCount,
                MaxSessionCount,
                MinSessionCount,
                isHourlyAverage: startDate.isSame(endDate, "day"),
            };
        },
        getXAxisData() {
            const { buckets = [], timeSeriesType } = this.data;
            log("getXAxisData.buckets", buckets);
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;
            log("getSeries.buckets", buckets);
            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => bucket.ticketCount);
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: "rgb(55, 162, 255)",
                            },
                            {
                                offset: 1,
                                color: "rgb(255,255,255)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;
            log("exportToExcel.buckets", buckets);
            const data = buckets.map((bucket) => {
                const ticketCount = bucket.ticketCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    ticketCount,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam oturum",
                    ticketCount: stats.TotalSessionCount,
                },
                {
                    date: "Ortalama oturum",
                    ticketCount: stats.AverageSessionCount,
                },
                {
                    date: "Maksimum oturum",
                    ticketCount: stats.MaxSessionCount,
                },
                {
                    date: "Minimum oturum",
                    ticketCount: stats.MinSessionCount,
                },
            ];
            expertToExcel(excelData, `chat_session_count_timeseries.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".ticket-count-time-series-chart"),
            name: "wave",
        });
        this.stats = this.getStats();
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.ticket-count-time-series-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.ticket-count-time-series-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
